<script lang="ts">
  /**
   * The screen that has to be fast. Date defaults to today and hours to a
   * sensible step, so the common case is: tap a client, tap Log.
   */
  import { untrack } from 'svelte';
  import { today, formatHours, roundHours } from '$shared/dates';
  import { store } from '$lib/store.svelte';
  import NoteInput from './NoteInput.svelte';

  interface Props {
    /** Prefilled from the timer when a session is stopped. */
    initialHours?: number;
    initialNotes?: string;
    onlogged?: () => void;
  }

  let { initialHours, initialNotes, onlogged }: Props = $props();

  // Captured once, on purpose: these seed the form and are then owned by it.
  // The parent remounts this component (via `{#key}`) to push a new prefill,
  // which is simpler than syncing props into state with effects.
  let date = $state(today());
  let hours = $state(untrack(() => initialHours) ?? 1);
  let notes = $state(untrack(() => initialNotes) ?? '');
  let saving = $state(false);
  let justLogged = $state(false);

  const isToday = $derived(date === today());
  const canSave = $derived(hours > 0 && !!store.activeClientId && !saving);

  function bump(delta: number) {
    hours = Math.max(0, roundHours(hours + delta));
  }

  async function submit(event: SubmitEvent) {
    event.preventDefault();
    if (!canSave) return;

    saving = true;
    try {
      await store.addEntry({ date, hours, notes: notes.trim() });
      // Reset for the next log, but keep the date — people often backfill a
      // few entries for the same past day in one sitting.
      notes = '';
      hours = 1;
      justLogged = true;
      setTimeout(() => (justLogged = false), 1600);
      onlogged?.();
    } catch (err) {
      store.error = err instanceof Error ? err.message : String(err);
    } finally {
      saving = false;
    }
  }
</script>

<form class="card stack" onsubmit={submit}>
  <div class="grid">
    <div>
      <label for="qe-date">Date</label>
      <input id="qe-date" type="date" bind:value={date} max={today()} />
      {#if !isToday}
        <button type="button" class="ghost today-link" onclick={() => (date = today())}>
          Back to today
        </button>
      {/if}
    </div>

    <div>
      <label for="qe-hours">Hours</label>
      <div class="stepper">
        <button type="button" onclick={() => bump(-0.5)} aria-label="Half an hour less">−</button>
        <input
          id="qe-hours"
          class="num"
          type="number"
          inputmode="decimal"
          step="0.5"
          min="0"
          bind:value={hours}
        />
        <button type="button" onclick={() => bump(0.5)} aria-label="Half an hour more">+</button>
      </div>
      <div class="presets">
        {#each [1, 2, 4, 6, 8] as preset (preset)}
          <button
            type="button"
            class="preset"
            class:active={hours === preset}
            onclick={() => (hours = preset)}
          >
            {preset}h
          </button>
        {/each}
      </div>
    </div>
  </div>

  <div>
    <label for="qe-notes">Notes</label>
    <NoteInput id="qe-notes" bind:value={notes} suggestions={store.noteSuggestions} />
  </div>

  <div class="spread">
    <span class="muted small">
      {#if store.todayHours > 0}
        {formatHours(store.todayHours)} logged today
      {:else}
        Nothing logged today
      {/if}
    </span>
    <button class="primary" type="submit" disabled={!canSave}>
      {#if saving}Saving…{:else if justLogged}Logged ✓{:else}Log {formatHours(hours)}{/if}
    </button>
  </div>
</form>

<style>
  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.9rem;
  }

  @media (max-width: 30rem) {
    .grid {
      grid-template-columns: 1fr;
    }
  }

  .stepper {
    display: grid;
    grid-template-columns: var(--tap) 1fr var(--tap);
    gap: 0.4rem;
  }

  .stepper button {
    padding: 0;
    font-size: 1.3rem;
    line-height: 1;
  }

  .stepper input {
    text-align: center;
    font-weight: 650;
    /* Spinners duplicate the ± buttons and shrink the tap target. */
    appearance: textfield;
    -moz-appearance: textfield;
  }

  .stepper input::-webkit-outer-spin-button,
  .stepper input::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }

  .presets {
    display: flex;
    gap: 0.35rem;
    margin-top: 0.45rem;
    flex-wrap: wrap;
  }

  .preset {
    flex: 1;
    min-width: 3rem;
    min-height: 36px;
    padding: 0.3rem 0.4rem;
    font-size: 0.85rem;
    background: transparent;
  }

  .preset.active {
    background: var(--surface-2);
    border-color: var(--accent);
    color: var(--accent);
    font-weight: 650;
  }

  .today-link {
    margin-top: 0.35rem;
    min-height: 32px;
    padding: 0 0.2rem;
    font-size: 0.8rem;
    color: var(--accent);
  }

  .small {
    font-size: 0.85rem;
  }
</style>
